<template>
  <div :class="['app-shell', currentSeason.id]">
    <div class="layout-shell">
      <header class="layout-header">
        <div class="header-pulse"></div>
        <div class="header-inner">
          <div class="season-title-block">
            <h1 class="season-title__heading">{{ currentSeason.scene }}</h1>
            <p class="season-title__sub">{{ currentSeason.description }}</p>
          </div>
          <nav class="season-nav">
            <button
              v-for="season in seasons"
              :key="season.id"
              type="button"
              :class="{ active: season.id === currentSeason.id }"
              @click="selectSeason(season)"
            >
              <span class="season-nav__label">{{ season.label }}</span>
              <span class="season-nav__tradition">{{ season.tradition }}</span>
            </button>
          </nav>
        </div>
      </header>

      <main class="layout-main">
        <section class="main-left">
          <div v-if="currentSeasonPanel" class="season-panel">
            <div class="season-panel__intro-block">
              <p class="season-panel__tag">{{ currentSeasonPanel.tag }}</p>
              <h2>{{ currentSeasonPanel.heading }}</h2>
              <div class="season-panel__description">
                <div v-for="(paragraph, index) in currentSeasonPanel.descriptionParagraphs"
                  :key="`${currentSeasonPanel.heading}-desc-${index}`" class="season-panel__description-paragraph">
                  <p>{{ paragraph }}</p>
                </div>
              </div>
            </div>

            <div v-if="currentSeasonPanel.sections && currentSeasonPanel.sections.length"
              class="season-panel__grid-block">
              <nav class="season-panel__grid">
                <article v-for="section in currentSeasonPanel.sections" :key="section.title" class="season-panel__card"
                  role="button" tabindex="0" @click="handleSectionClick(section)"
                  @keydown.enter.prevent="handleSectionClick(section)" @mouseenter="createRipple">
                  <h3>{{ section.title }}</h3>
                  <p>{{ section.description }}</p>
                </article>
              </nav>
            </div>

            <div v-if="currentSeasonPanel.masters" class="season-panel__masters-block">
              <div class="season-panel__masters-header">
                <p class="season-panel__featured-label">名师风采</p>
                <h3>春令代表 20 位</h3>
              </div>
              <div class="season-panel__masters-grid">
                <article v-for="master in currentSeasonPanel.masters" :key="master.name">
                  <div class="season-panel__masters-main">
                    <div class="season-panel__masters-title">
                      <strong>{{ master.name }}</strong>
                      <span>{{ master.era }}</span>
                    </div>
                    <p class="season-panel__masters-contribution">{{ master.contribution }}</p>
                    <p class="season-panel__masters-note">{{ master.note }}</p>
                  </div>
                  <div class="season-panel__masters-overlay">
                    <p class="season-panel__masters-overlay-title">详细简介</p>
                    <p>{{ master.note }}</p>
                  </div>
                </article>
              </div>
            </div>
            <div v-if="sectionNotice" :class="[
              'season-panel__notice',
              {
                'season-panel__notice--red': sectionNotice.includes('内容整理中'),
              },
            ]">
              {{ sectionNotice }}
            </div>
          </div>
          <div v-else class="wisdom-stack">
            <article v-for="section in wisdomSections" :key="section.title">
              <h3>{{ section.title }}</h3>
              <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
            </article>
          </div>

          <div class="poem-block">
            <p v-for="line in currentSeasonPoem" :key="line">{{ line }}</p>
          </div>
        </section>

        <aside class="main-right">
          <div class="time-block">
            <p class="time-value">{{ currentTime }}</p>
            <p class="time-season">当前时节 · {{ currentMomentSeason.label }}</p>
          </div>

          <div class="lunar-block">
            <div class="lunar-summary">
              <p class="lunar-line">农历 · {{ lunarDisplay }}</p>
              <p class="remote-note" v-if="remoteLunarLoading">正在获取权威农历信息…</p>
              <p class="remote-error" v-else-if="remoteLunarError">{{ remoteLunarError }}</p>
            </div>
            <div v-if="!remoteLunarLoading && !remoteLunarError && hasRemoteLunarData" class="remote-lunar">
              <div v-for="line in remoteLunarLines" :key="line.raw" class="remote-line">
                <span v-if="line.label" class="remote-label">{{ line.label }}</span>
                <span class="remote-value">{{ line.value }}</span>
              </div>
            </div>
          </div>

          <div class="scene-block">
            <div class="scene-message">
              <span class="scene-title">{{ currentSeason.scene }}</span>
              <span class="scene-caption">{{ currentSeason.description }}</span>
            </div>
          </div>

          <div class="hourly-card">
            <div class="hourly-media" v-if="currentHourlyGuide.image">
              <img :src="currentHourlyGuide.image" :alt="currentHourlyGuide.title" loading="lazy" />
            </div>
            <div class="hourly-content">
              <p class="hourly-time">{{ currentHourlyGuide.timeLabel }}</p>
              <p class="hourly-label">{{ currentHourlyGuide.label }}</p>
              <h4 class="hourly-title">{{ currentHourlyGuide.title }}</h4>
              <p class="hourly-verse" v-if="currentHourlyGuide.verse">{{ currentHourlyGuide.verse }}</p>
              <p class="hourly-description">{{ currentHourlyGuide.description }}</p>
              <p class="hourly-highlight">{{ currentHourlyGuide.highlight }}</p>
            </div>
          </div>
        </aside>
      </main>

      <footer class="layout-footer">
        <p>© 2025 天诚品 · 五季有诚 · 以德立道 </p>
        <div class="footer-links">
          <a href="#" target="_blank">版权声明</a>
          <a href="#" target="_blank">友链 · 心境</a>
          <a href="#" target="_blank">联系我们</a>
          <a href="https://blog.tcp8.cn/" target="_blank">BLOG</a>
          <a href="https://bbs.tcp8.cn/" target="_blank">编程论坛</a>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { solarToLunar } from 'vue-solar-lunar'
import { hourlyGuides, hourlyGuideLink } from './data/hourlyGuide'

const heavenlyStems = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸']
const earthlyBranches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥']

const WENKU_SEASONS = new Set(['spring', 'summer', 'longsummer', 'autumn', 'winter'])

export default {
  name: 'App',
  data() {
    const seasons = [
      {
        id: 'harmony',
        label: '合吟',
        tradition: '合',
        scene: '合吟 · 五季同声',
        description: '以四时为律，五行共鸣，合吟而生，调匀万象之声。',
      },
      {
        id: 'spring',
        label: '春',
        tradition: '医',
        scene: '春发芽 · 绿意初生',
        description: '嫩芽破土，风带着暖阳与花香。',
      },
      {
        id: 'summer',
        label: '夏',
        tradition: '儒',
        scene: '夏成长 · 荷叶扶疏',
        description: '炎阳熠熠，雨露与汗水一起滋养。',
      },
      {
        id: 'longsummer',
        label: '长夏',
        tradition: '释',
        scene: '长夏绵绵 · 温润归真',
        description: '云卷云舒，气息缓慢而充盈。',
      },
      {
        id: 'autumn',
        label: '秋',
        tradition: '道',
        scene: '秋丰收 · 稻谷金黄',
        description: '凉风拂过，果实将光与时间酿成甘露。',
      },
      {
        id: 'winter',
        label: '冬',
        tradition: '易',
        scene: '冬收藏 · 白雪轻舞',
        description: '银霜漫天，静默是厚重的等待。',
      },
    ]

    const seasonLookup = seasons.reduce((map, season) => {
      map[season.id] = season
      return map
    }, {})

    const seasonPanels = {
      harmony: {
        tag: '合吟 · 五季共鸣',
        heading: '和声共生',
        descriptionParagraphs: [
          '春以精诚启生机，生者不息，自蕴无穷；',
          '夏以担当立正道，行者无畏，挺膺向前；',
          '长夏以安宁居其中，守静以观，照见本心；',
          '秋以清素收万象，知止而定，取舍有度；',
          '冬以韬略蓄其势，潜龙在渊，成于未形。',
          '',
          '五季循环，如天行不辍；',
          '诚贯其间，如北辰不移。',
          '',
          '诚，可启生发，使枯木逢春；',
          '诚，可毅成事，使重担化轻；',
          '诚，可澄内心，使尘念皆息；',
          '诚，可守本真，使风霜难侵；',
          '诚，可定胜局，使未战先胜。',
          '',
          '天地以诚而立，',
          '万物以诚而生，',
          '人心以诚而安。',
          '',
          '是故，立诚者，乃心之大任：',
          '为天地立心，令阴阳有序、生生不息；',
          '为生民立命，使万众安身、信义可托；',
          '为往圣继绝学，使道统不断、文化长存；',
          '为万世开太平，让后人无忧、山河久安。',
          '',
          '诚者立身之根，成人之基，通道之钥。',
          '诚若长河，涵万象而不息；',
          '诚如大光，照乾坤而不灭。',
          '',
          '',

        ],
      },
      spring: {
        tag: '春 · 中医篇：生发与身体之道',
        heading: '大医精诚',
        descriptionParagraphs: [
          '春风启蛰，万物生发，诚者先动人心。',
          '寸口关尺，浮沉弦紧，脉脉皆天机；',
          '盈虚寒热，血脉荣卫，丝毫不可忽。',
          '对病者如至亲，见其痛若己伤，寒暑无辞，饥渴不辞。',
          '无贵贱无远近，万灵同一体；',
          '不以名利动，不以成败惊',
          '精勤入微，慈恻为本；大医之诚，胜于百药。',
          '乃知春之生发，由心之精诚。',

        ],
        sections: [
          { title: '医案', description: '精选春令医案，呈现节令调摄与辨证裁治。', link: '/spring/medical-cases' },
          { title: '集录', description: '集录医家谈录，将春日养生经验归档存照。', link: '/spring/collections' },
          { title: '典藏', description: '珍藏善本与图谱，佐以草木之性与经络图示。' },
          { title: '针灸', description: '针灸专案顺春风通络，疏肝理气，调脉达乎。' },
          { title: '条辩', description: '条辨春季流行证候，厘清本与标的差别。' },
          { title: '经方', description: '经方疏养肝胆、扶阳托生，顺气承春之意。', link: '/spring/formulas' },
          { title: '经脉', description: '经脉调理重在承载新生之气，润泽筋骨与脉络。' },
          { title: '炮炙', description: '炮炙草药工艺，因时制宜，顺应春日清阳。' },
          { title: '方解', description: '方解讲述草木性味与方剂运用，助春日辨证施治。', link: '/spring/signatures' },
        ],
        masters: [
          {
            name: '岐伯',
            era: '时代：传说中黄帝时期',
            contribution: '作为《黄帝内经》的重要对话者，阐述人体生理、病理、阴阳五行、经络系统等基础理论。',
            note: '思想价值：奠定中医整体观、天人合一、气血运行、治未病的根本框架；影响：被尊为中医理论之源。',
          },
          {
            name: '扁鹊（秦越人）',
            era: '时代：战国',
            contribution: '完善望闻问切四诊法，开创脉诊体系，提出“六不治”。',
            note: '经典逸事：蔡桓公治疗争议、起死回生救虢太子；影响：称为“医祖”。',
          },
          {
            name: '华佗',
            era: '时代：东汉',
            contribution: '被称外科先驱，创造麻醉药“麻沸散”与五禽戏养生法。',
            note: '典故：为关羽刮骨疗伤、曹操头疾未得施术；评价：被誉为“外科圣手”。',
          },
          {
            name: '张仲景',
            era: '时代：东汉末',
            contribution: '著《伤寒杂病论》，确立辨证论治体系与六经辨证模式。',
            note: '代表思想：方随证走、法从方出；评价：被尊为“医圣”。',
          },
          {
            name: '皇甫谧',
            era: '时代：西晋',
            contribution: '编著《针灸甲乙经》，整理针灸穴位、经络与操作规范。',
            note: '影响：确立针灸理论体系；地位：针灸鼻祖。',
          },
          {
            name: '葛洪',
            era: '时代：东晋',
            contribution: '著有急症处方与应用的《肘后备急方》，并强调预防与应急医学。',
            note: '评价：急救医学之祖；代表作：《抱朴子》。',
          },
          {
            name: '孙思邈',
            era: '时代：唐朝',
            contribution: '著《千金要方》《千金翼方》，提出“大医精诚”医德纲领并强调养生预防。',
            note: '影响：药学与伦理并重，被称“药王”。',
          },
          {
            name: '钱乙',
            era: '时代：北宋',
            contribution: '《小儿药证直诀》确立儿科独立体系与小儿辨证方法。',
            note: '核心思想：“小儿脏腑柔弱，易虚易实”；影响：儿科之圣。',
          },
          {
            name: '李时珍',
            era: '时代：明代',
            contribution: '编撰《本草纲目》，总结1892种药物与方义。',
            note: '意义：为现代药物学、植物学、动物分类学奠基；评价：东方达尔文级科学贡献。',
          },
          {
            name: '叶天士',
            era: '时代：清代',
            contribution: '开创温病学派，撰写《温热论》，提出卫气营血辨证。',
            note: '影响：启发现代传染病与中医抗疫理论。',
          },
          {
            name: '王清任',
            era: '时代：清末',
            contribution: '实际解剖研究纠正中医误解；著《医林改错》《血证论》。',
            note: '思想：重视血瘀理论，推动临床真实化；影响：推动医学科学化。',
          },
          {
            name: '张锡纯',
            era: '时代：清末民初',
            contribution: '中西医结合倡导者，吸收西医理论用于中医体系。',
            note: '著作：《医学衷中参西录》；影响：开创现代跨学科思维。',
          },
          {
            name: '丁甘仁',
            era: '时代：民国',
            contribution: '四大名医之一，急症治疗大师，强调辨证实效。',
            note: '影响：“活人无数”。',
          },
          {
            name: '施今墨',
            era: '时代：民国',
            contribution: '四大名医代表，擅长难治杂病；留下大量临床案例。',
            note: '代表作：难治杂病病例集。',
          },
          {
            name: '秦伯未',
            era: '时代：现代',
            contribution: '易水派提出者，推进中医理论革新。',
            note: '著作：《中医基础理论的新探索》；意义：促进理论进化。',
          },
          {
            name: '邓铁涛',
            era: '时代：当代',
            contribution: '推动现代中医教育、政策与中西医交流。',
            note: '称号：岭南中医巨擘。',
          },
          {
            name: '胡希恕',
            era: '当代',
            contribution: '伤寒派重建者，主张原文原方应用。',
            note: '著作：《胡希恕伤寒论讲解》；价值：点燃经典回归运动。',
          },
          {
            name: '叶任高',
            era: '当代',
            contribution: '现代针灸研究与标准化推动者，强调国际化教育。',
            note: '成果：以针灸科学实验为基础的现代教育体系。',
          },
          {
            name: '裘沛然',
            era: '当代',
            contribution: '温病与学理化表达的推进者，提升中医语言表达。',
            note: '贡献：现代学术表达与传播。',
          },
          {
            name: '倪海厦',
            era: '当代',
            contribution: '系统输出中医经典教育、提出天纪体系与“原味经方”主张。',
            note: '代表作：《经方现代中医全集》《天纪讲义》《人纪讲义》；影响：推动中医回归经典与实证。',
          },
        ],
      },
      summer: {
        tag: '夏 · 儒家篇：成长与担当之道',
        heading: '阳承礼法',
        descriptionParagraphs: [
          '日长天盛，烈阳在上，诚者立于光明。',
          '礼以正身，义以定志，进退皆为担当。',
          '少壮不惧热，挺膺直面难；',
          '扶危济困，不求名誉，担当即为诚。',
          '言必信，行必果，',
          '光明磊落，堂堂正正。',
          '人得其诚，则众心可聚；心得其诚，则炎夏不灼。',


        ],
        sections: [
          { title: '修身', description: '立德砺行，担当之始 日长天盛，烈阳在上，诚者立于光明。礼以正身，义以定志，进退皆为担当。少壮不惧热，挺膺直面难；扶危济困，不求名誉，担当即为诚。言必信，行必果，光明磊落，堂堂正正。人得其诚，则众心可聚；心得其诚，则炎夏不灼。' },
          { title: '齐家', description: '家教与传承 承先启后，家庭伦理与礼仪在夏日中显得格外炽盛。家风以德立，家教以礼化，温情中存敬畏。修身以安家，齐家以育德，让担当从日常起步。' },
          { title: '治国', description: '任责与仁心 儒者强调“修身齐家治国平天下”，以德化民，以礼服人。担当不仅是为己之志，更是为群之责。以仁政养民心，以诚信固邦本，使民不惧炎夏，安其所安。' },
          { title: '平天下', description: '兼济苍生 勇承责任，教养学子以勇毅与敬畏之心迎接炎热与挑战。心怀大同，济世利民，志不在名而在公。' },
          { title: '礼乐', description: '秩序与和声 夏日祭祀与乐舞，用礼乐之声稳固社会节律。礼乐相和，风化自然，明德以安众，合乐以定心。德治行于外，礼乐润于内，盛夏亦成和光。' },
        ],
      },
      longsummer: {
        tag: '长夏 · 佛家篇：居中与觉悟之道',
        heading: '寂照无为',
        descriptionParagraphs: [
          '天地居中，万物安宁。诚者不躁，不争，无为而成。',
          '尘念既息，则心如明镜；',
          '欲海既平，则道自清凉。',
          '不求外功，不饰虚名，',
          '以寂照自省，以无为化成。',
          '无声中成事，寂静里见光，',
          '诚若深泉，涵万物而不言。',

        ],
        sections: [
          { title: '禅心', description: '禅心者，不为境迁，不随念起。长夏静坐，以息观心；尘念既寂，则心如明镜，照而不取，观而不执。坐忘诸相，放下诸缘，不住于色声香味触法；心若虚空，万物自安。能制其心，则无事不成；能守其意，则无境可扰。' },
          { title: '慈悲', description: '慈是无量，悲是同体；悲悯众苦，视众生如一子。以柔克刚，以善止怒，以愿化怨，在夏之炎躁中抚慰群心，使凉风入骨，令安宁遍界。广施无畏，福慧双修；含容众生，不求回报。慈悲者，利他即自利，救世即自度' },
          { title: '中道', description: '中道者，离二边，不落断常，阴阳得和，苦乐俱舍。住于中正，无增无减；法不增一，心不失一。处事不偏，立意不倚；进退皆度，得失皆空。若能行中道，则天地位焉，万物育焉，心安故事成。' },
          { title: '内观', description: '内观者，返观自心，非求于外。照见五蕴皆空，则烦恼不起；观呼吸往来，则觉性常住。妄念若烟，观之则散；真心如月，云尽自明。内照则智慧开启，外求则迷失本源。洞见本心，即得解脱。' },
          { title: '觉悟', description: '觉悟者，明见无常，透达缘起，悟生灭即寂灭。无为而化，无声而成，不饰虚名，不求外功；欲海既平，道体自现。寂照中光明自生，热极则凉，动极则静，迷尽则觉。长夏之时，是返观与解脱之善巧方便，是觉性安住与智慧成就之佳期。' },
        ],
      },
      autumn: {
        tag: '秋 · 道家篇：收敛与取舍之道',
        heading: '归素自守',
        descriptionParagraphs: [
          '秋风肃然，大地归根。诚者知止，善于取舍。',
          '去浮华，返真实。',
          '减欲望，存本心。',
          '收敛锋芒，守道如松；百炼之后，方见本真。',
          '不随世潮，不逐虚荣，',
          '唯守一颗清素之心。',
          '是为秋之诚，收而不乱，守而不失。',
        ],
        sections: [
          { title: '归素', description: '秋气肃然，万物归根。智者知止，善乎取舍；去浮华以返本真，减嗜欲以存本心。敛锋芒若松之定，沉百炼而后见真。不随世潮，不逐虚誉，惟守清素一心。是为秋之诚：收而不乱，守而不失。' },
          { title: '归藏', description: '顺时而收，藏神守气；不争强，不好胜；知退乃知进，蓄势而待时，沉潜以养真。' },
          { title: '无为', description: '道法自然，贵少私寡欲；以柔制刚，以静制躁；无为非无事，乃不扰、不越、不过；守静观化，处顺应变。' },
          { title: '养隐', description: '秋成之后，宜息心养志；以闲以逸，清神养性；不求显赫，但居内明；不耀于外，而光生于中。' },
          { title: '节制', description: '秋令主收，节度为要；以道律心得盈虚，以度量行乎进退；以清谈化燥，以静默安心；守之则固，节之则成。' },
        ],
      },
      winter: {
        tag: '冬 · 易经篇：蓄势与策略之道',
        heading: '蓄势之谋',
        descriptionParagraphs: [
          '大雪封山，万籁俱寂。诚者不息，不弃，不怠。',
          '蓄锐养精，不显于外；',
          '韬光潜行，以静制动。',
          '策未出而势已成，兵未动而胜已决。',
          '外冰如铁，内火如龙，',
          '以诚定其谋，以信成其事。',
          '守此冬实，待来春生。',
        ],
        sections: [
          { title: '蓄势', description: '大雪闭塞，天地潜藏。君子处此，不怠不弃，敛锋以蓄锐，藏器以待时；韬光以守静，不露其端；定心以持志，不失其本。谋未形而势已成，兵未动而胜可期。外寒如铁，内火如龙；以诚立基，以信成事。守冬之实，俟春之萌，斯乃蓄势之道也。' },
          { title: '潜龙', description: '龙潜于渊，晦迹不彰；静以养元，默以积力。不以一时之失意而改志，不为外象之扰动其心。时未至则深藏不出，一息不泄锋芒；时既至则乘风破冰，腾跃九霄。潜者非退也，乃蓄也；隐者非弱也，乃强也。' },
          { title: '图策', description: '图远者谋全局，识时者审进退。稽古观今，问道于贤；因时制宜，以势驭势。不争旦夕之短长，不以小利乱大谋。见龙在田，群贤毕至，和力而成其功；能合众心者，能成大业。' },
          { title: '观变', description: '天地有序，阴阳有度，消长往复，无常而常。君子观四时寒暑之变，察万象消息之机；识变于微兆，见机于未萌。以象推理，以道御变，故能立于不败之地。知进退存亡而不失其正者，圣人之智也。' },
          { title: '再生', description: '否极则泰，寒极则春，冬尽则万物并作。蓄满则势发，积微则成著；一息积久而动乾坤，一势潜长而成大变。机至则应，时来则发，不逾其度，不失其时。故曰：守静以待时，积势以再生。' },
        ],
      },
    }


    const seasonPoems = {
      harmony: [
        '诚者，天之道也；思诚者，人之道也。',
        '天地以诚而运，万物以诚而生，人以诚而立命。',
      ],
      spring: [
        '春气生，草木萌，肝木条达养真情。',
        '扶阳气，顺肝行，不负天地发生令。',
        '诚于身，谨养精，调和气血以自明。',
      ],
      summer: [
        '日中则昃，盛极则变；君子务本，本立而道生。',
        '火明德，礼立身，赤子之心不敢欺。',
        '大人者，以诚为仁，以诚为勇，以诚为行。',
      ],
      longsummer: [
        '一念清净，花开世界；一心真实，万法皆成。',
        '中道不偏，觉性常明，慈悲不虚，智慧自生。',
        '真心无伪，明眼看破，能除贪嗔痴三毒。',
      ],
      autumn: [
        '秋风肃肃，金气断割；见性明静，自守其真。',
        '少则得，多则惑；损之又损，至于无为。',
        '顺乎自然，不假饰，不伪作，不逆时。',
      ],
      winter: [
        '一阳来复，元亨利贞；藏器于身，待时而动。',
        '诚者物之终始，不诚无物，变与守，动与静。',
        '大哉乾元，万物资始，乃统天命。',
      ],
    }

    Object.keys(seasonPoems).forEach(key => {
      if (seasonPanels[key]) {
        seasonPanels[key].poemLines = seasonPoems[key]
      }
    })

    return {
      seasons,
      seasonLookup,
      seasonPanels,
      selectedSeasonId: 'harmony',
      now: new Date(),
      timer: null,
      sectionNotice: '',
      sectionNoticeTimer: null,
      wisdomSections: [
        {
          title: '儒家 —— 以诚立德，以品化人',
          paragraphs: [
            '儒曰：诚者，天之大经，地之常道；蕴于人心，则化为品性。',
            '夫能存诚以修身，秉德以立世，则与天同其久，与道同其光。',
          ],
        },
        {
          title: '释家 —— 以真显性，以诚证觉',
          paragraphs: [
            '释曰：众生本具真如，圆明不昧；因迷起幻，由觉归诚。',
            '若能返妄归真，照见本心，则万法皆空而灵慧自显，其品清净如莲出水。',
          ],
        },
        {
          title: '道家 —— 以自然为真，以无为为品',
          paragraphs: [
            '道曰：大朴不雕，大象无形；天道至诚，不言而信。',
            '能澄心抱元，返本复初，则与天地同和，与万物并生，其品冲虚若谷，清静不争。',
          ],
        },
        {
          title: '医家 —— 以和为道，以诚养生',
          paragraphs: [
            '医曰：天给予命，诚守其元；和者，生之本，偏者，病之源。',
            '医之大道，贵在调阴阳、和营卫，使气血以畅，神志以安，则品命得全，寿可与日月长。',
          ],
        },
      ],
      poem: [
        '????????????????????',
        '????????????????????',
      ],
      remoteLunarLines: [],
      remoteLunarLoading: true,
      remoteLunarError: '',
      hourlyGuides,
      hourlyGuideLink,
    }
  },
  computed: {
    currentSeason() {
      return this.seasonLookup[this.selectedSeasonId]
    },
    currentSeasonPanel() {
      return this.seasonPanels[this.selectedSeasonId] || null
    },
    currentSeasonPoem() {
      if (this.currentSeasonPanel && this.currentSeasonPanel.poemLines) {
        return this.currentSeasonPanel.poemLines
      }
      return this.poem
    },
    currentTime() {
      return this.now.toLocaleString('zh-CN', {
        hour12: false,
      })
    },
    currentMomentSeason() {
      return this.determineSeason(this.now)
    },
    lunarInfo() {
      try {
        return solarToLunar(this.now.getFullYear(), this.now.getMonth() + 1, this.now.getDate())
      } catch (error) {
        return null
      }
    },
    lunarDisplay() {
      if (!this.lunarInfo) {
        return '载入中...'
      }
      return `${this.lunarInfo.year}年 ${this.lunarInfo.monthStr}${this.lunarInfo.dayStr}`
    },
    lunarGanzhi() {
      if (!this.lunarInfo) {
        return '载入中...'
      }
      const year = this.lunarInfo.year
      const stemIndex = ((year - 4) % 10 + 10) % 10
      const branchIndex = ((year - 4) % 12 + 12) % 12
      return `${heavenlyStems[stemIndex]}${earthlyBranches[branchIndex]}`
    },
    lunarSolarTerm() {
      if (!this.lunarInfo) {
        return '载入中...'
      }
      return this.lunarInfo.Terms || '无节气'
    },
    hasRemoteLunarData() {
      return this.remoteLunarLines.some(line => {
        const label = (line.label || '').trim()
        const value = (line.value || '').trim()
        return label.length > 0 || value.length > 0
      })
    },
    currentHourlyGuide() {
      const hour = this.now.getHours()
      return this.hourlyGuides.find(guide => guide.hour === hour) || this.hourlyGuides[0]
    },
  },
  methods: {
    selectSeason(season) {
      this.selectedSeasonId = season.id
    },
    determineSeason(date) {
      const month = date.getMonth() + 1
      if (month >= 3 && month <= 5) {
        return this.seasonLookup.spring
      }
      if (month >= 6 && month <= 7) {
        return this.seasonLookup.summer
      }
      if (month === 8) {
        return this.seasonLookup.longsummer
      }
      if (month >= 9 && month <= 10) {
        return this.seasonLookup.autumn
      }
      return this.seasonLookup.winter
    },
    startClock() {
      this.timer = setInterval(() => {
        this.now = new Date()
      }, 1000)
    },
    handleSectionClick(section) {
      if (WENKU_SEASONS.has(this.selectedSeasonId)) {
        this.openWenkuWindow()
        return
      }
      if (section.link) {
        if (this.$router && typeof this.$router.push === 'function') {
          this.$router.push(section.link)
        } else {
          window.location.href = section.link
        }
        return
      }
      this.showSectionNotice(`${section.title} 内容整理中`)
    },
    openWenkuWindow() {
      const defaultPath = '/wenku'
      let targetHref = defaultPath
      if (this.$router && typeof this.$router.resolve === 'function') {
        const resolved = this.$router.resolve({ path: defaultPath })
        if (resolved && resolved.href) {
          targetHref = resolved.href
        }
      }
      window.open(targetHref, '_blank', 'noopener,noreferrer')
    },
    showSectionNotice(message) {
      this.sectionNotice = message
      if (this.sectionNoticeTimer) {
        clearTimeout(this.sectionNoticeTimer)
      }
      this.sectionNoticeTimer = setTimeout(() => {
        this.sectionNotice = ''
        this.sectionNoticeTimer = null
      }, 3200)
    },
    createRipple(event) {
      const card = event.currentTarget
      card.querySelectorAll('.ripple').forEach(ripple => ripple.remove())
      const diameter = Math.max(card.clientWidth, card.clientHeight)
      const radius = diameter / 2
      const circle = document.createElement('span')
      circle.className = 'ripple'
      circle.style.width = circle.style.height = `${diameter}px`
      circle.style.left = `${event.offsetX - radius}px`
      circle.style.top = `${event.offsetY - radius}px`
      card.appendChild(circle)

      setTimeout(() => {
        circle.remove()
      }, 600)
    },
    fetchRemoteLunarInfo() {
      if (typeof fetch === 'undefined') {
        this.remoteLunarLoading = false
        this.remoteLunarError = '当前环境不支持 fetch，无远程数据。'
        return
      }
      this.remoteLunarLoading = true

      const apiPath = '/tools/time.php'
      fetch(apiPath, { cache: 'no-cache' })
        .then(response => response.text())
        .then(text => {
          const html = text.replace(/^\uFEFF/, '').trim()
          if (html) {
            this.remoteLunarLines = this.parseRemoteLines(html)
            if (this.remoteLunarLines.length) {
              this.remoteLunarError = ''
            } else {
              this.remoteLunarError = '远程数据格式异常，继续展示本地数据。'
            }
          } else {
            this.remoteLunarError = '远程数据格式异常，继续展示本地数据。'
          }
        })
        .catch(error => {
          console.warn('远程农历数据获取失败：', error)
          this.remoteLunarError = '远程农历数据获取失败，继续展示本地数据。'
        })
        .finally(() => {
          this.remoteLunarLoading = false
        })
    },
    parseRemoteLines(rawHtml) {
      const segments = rawHtml
        .split(/<\/?br\s*\/?>/gi)
        .map(part => part.replace(/&nbsp;/g, ' ').trim())
        .map(part => part.replace(/\s+/g, ' '))
        .filter(Boolean)

      return segments.map(part => {
        const colonMatch = part.match(/[:：]/)
        if (colonMatch) {
          const colonIndex = part.indexOf(colonMatch[0])
          const label = part.slice(0, colonIndex + 1).trim()
          const value = part.slice(colonIndex + 1).trim()
          return { label, value, raw: part }
        }
        return { label: '', value: part, raw: part }
      })
    },
  },
  created() {
    this.selectedSeasonId = this.determineSeason(this.now).id
  },
  mounted() {
    this.startClock()
    this.fetchRemoteLunarInfo()
  },
  beforeDestroy() {
    clearInterval(this.timer)
    if (this.sectionNoticeTimer) {
      clearTimeout(this.sectionNoticeTimer)
    }
  },
}
</script>

<style>
:root {
  font-family: 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

html,
body,
#app {
  height: 100%;
  margin: 0;
}

.app-shell {
  min-height: 100vh;
  width: 100%;
  background: var(--page-bg, #05060a);
  color: var(--text-color, #121212);
  transition: background 1s ease, color 1s ease;
  display: flex;
  justify-content: stretch;
  padding: 0;
}

.layout-shell {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), transparent 60%);
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
}

  .layout-header {
    position: relative;
    padding: 1.75rem 2rem 1.3rem;
    overflow: hidden;
    background: var(--header-bg, rgba(255, 255, 255, 0.12));
  }

  .header-pulse {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.3), transparent 60%);
    animation: pulse 8s linear infinite;
    opacity: 0.6;
  }

  .header-inner {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    z-index: 1;
  }

  .season-title-block {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    max-width: 560px;
  }

  .season-title__heading {
    margin: 0;
    font-size: 3rem;
    letter-spacing: 0.03em;
    line-height: 1.2;
    color: var(--text-color);
  }

  .season-title__sub {
    margin: 0;
    font-size: 0.95rem;
    opacity: 0.8;
  }

  .season-nav {
    position: relative;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.55rem;
    padding: 0.35rem;
    border-radius: 60px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.2);
    z-index: 1;
  }

  @media (max-width: 980px) {
    .season-title__heading {
      font-size: 2.4rem;
    }

    .season-nav {
      grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    }
  }

  .season-nav button {
    border: none;
    outline: none;
    width: 100%;
    padding: 0.45rem 0.6rem;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.4em;
    border-radius: 999px;
    cursor: pointer;
    background: transparent;
    color: inherit;
    transition: transform 0.35s ease, box-shadow 0.35s ease, background 0.35s ease;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
    text-transform: uppercase;
  }

  .season-nav button:hover {
    transform: translateY(-1px);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
  }

  .season-nav button.active {
    background: var(--accent);
    box-shadow: 0 0 25px rgba(193, 143, 0, 0.6);
    color: #1b0f00;
  }

  .season-nav__label {
    display: block;
    font-size: 1.35rem;
    letter-spacing: 0.4em;
    font-weight: 700;
  }

  .season-nav__tradition {
    display: block;
    font-size: 0.95rem;
    letter-spacing: 0.35em;
    font-weight: 600;
    color: rgba(41, 16, 5, 0.9);
  }

.layout-main {
  flex: 1;
  display: flex;
  gap: 1rem;
  padding: 1rem 1.25rem 1.5rem;
}

.main-left {
  flex: 0 0 70%;
  background: var(--main-left-bg, rgba(255, 255, 255, 0.1));
  border-radius: 22px;
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  color: inherit;
}

.main-left::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%,
      var(--scene-spark, rgba(255, 255, 255, 0.35)),
      transparent 45%);
  opacity: 0.6;
  animation: drift 14s ease-in-out infinite;
}

.time-block {
  position: relative;
  z-index: 1;
  margin-bottom: 1rem;
}

.time-value {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.time-season {
  font-size: 0.95rem;
  opacity: 0.85;
}

.lunar-block {
  position: relative;
  z-index: 1;
  padding: 0.8rem 1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.lunar-summary {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.lunar-line {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.08em;
}

.lunar-meta {
  margin-top: 0.35rem;
  display: flex;
  gap: 0.5rem;
  font-size: 0.85rem;
  opacity: 0.9;
}

.lunar-meta span {
  flex: 1;
  padding: 0.35rem 0.6rem;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.remote-lunar {
  line-height: 1.9;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: inherit;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
}

.remote-line {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: baseline;
}

.remote-label {
  min-width: 3.5rem;
  font-weight: 600;
  letter-spacing: 0.06em;
}

.remote-value {
  flex: 1;
  white-space: pre-wrap;
}

.remote-note,
.remote-error {
  margin-top: 0.6rem;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
}

.remote-note {
  color: rgba(255, 255, 255, 0.7);
}

.remote-error {
  color: #f5b359;
}

.scene-block {
  position: relative;
  z-index: 1;
  margin-bottom: 1.2rem;
  padding: 1rem 1.2rem;
  border-radius: 18px;
  background: var(--scene-bg, rgba(255, 255, 255, 0.08));
  box-shadow: inset 0 0 30px rgba(255, 255, 255, 0.2);
}

.scene-message {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.scene-title {
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 1px;
}

.scene-caption {
  font-size: 0.9rem;
  opacity: 0.8;
}

.wisdom-stack {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.wisdom-stack article {
  padding: 0.9rem 1rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.wisdom-stack h3 {
  margin: 0;
  font-size: 1rem;
  margin-bottom: 0.4rem;
}

.wisdom-stack p {
  margin: 0;
  line-height: 1.6;
  font-size: 0.95rem;
}

.season-panel {
  padding: 1.4rem;
  border-radius: 26px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.35), rgba(34, 69, 30, 0.55));
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 20px 40px rgba(9, 18, 6, 0.45);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.season-panel__intro-block {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.season-panel__tag {
  font-size: 0.75rem;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  opacity: 0.75;
  margin: 0;
}

.season-panel__intro-block h2 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
}

.season-panel__intro-block p {
  margin: 0;
  line-height: 1.5;
  font-size: 1rem;
  opacity: 0.85;
}

.season-panel__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.9rem;
}

.season-panel__card {
  padding: 0.85rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 0 12px rgba(255, 255, 255, 0.08);
  min-height: 120px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.season-panel__card h3 {
  margin: 0 0 0.35rem;
  font-size: 1rem;
}

.season-panel__card p {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.4;
  opacity: 0.85;
}

.season-panel__description {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.season-panel__description-paragraph {
  font-size: 1.15rem;
  font-weight: 500;
  line-height: 1.75;
  opacity: 0.95;
}

.season-panel__description-paragraph p {
  margin: 0;
}

.season-panel__grid-block {
  padding: 0.6rem;
  border-radius: 16px;
  background: rgba(15, 46, 24, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: inset 0 0 18px rgba(0, 0, 0, 0.2);
}

.season-panel__masters-block {
  padding: 0.6rem;
  border-radius: 16px;
  background: rgba(15, 46, 24, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: inset 0 0 18px rgba(0, 0, 0, 0.2);
}

.season-panel__masters-header {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.season-panel__masters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-top: 0.25rem;
}

.season-panel__masters-grid article {
  padding: 0.85rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.35s ease, box-shadow 0.35s ease, background 0.35s ease;
  position: relative;
  overflow: hidden;
}

.season-panel__masters-title {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.6rem;
}

.season-panel__masters-title strong {
  font-size: 0.95rem;
}

.season-panel__masters-title span {
  font-size: 0.75rem;
  opacity: 0.7;
}

.season-panel__masters-contribution,
.season-panel__masters-note {
  margin: 0.2rem 0 0;
  font-size: 0.85rem;
  line-height: 1.4;
  opacity: 0.85;
}

.season-panel__masters-overlay {
  position: absolute;
  inset: 0;
  background: rgba(13, 41, 18, 0.95);
  color: #f8fff6;
  padding: 1rem;
  border-radius: 12px;
  text-align: left;
  opacity: 0;
  transform: scale(0.98);
  transition: opacity 0.35s ease, transform 0.35s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.65rem;
  pointer-events: none;
}

.app-shell.harmony .season-panel__masters-overlay {
  background: rgba(255, 248, 232, 0.95);
  color: #3e2103;
}

.season-panel__masters-overlay-title {
  margin: 0;
  font-size: 0.9rem;
  letter-spacing: 0.5em;
  opacity: 0.7;
  text-transform: uppercase;
}

.season-panel__masters-grid article:hover .season-panel__masters-overlay {
  opacity: 1;
  transform: scale(1);
}

.season-panel__masters-grid article:hover {
  transform: scale(1.03);
  background: rgba(255, 255, 255, 0.14);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.season-panel__masters-grid article:hover strong {
  transform: scale(1.05);
}

.season-panel__masters-grid article:hover .season-panel__masters-contribution {
  font-size: 0.9rem;
  opacity: 0.95;
}

.season-panel__featured-block {
  padding: 0.5rem;
  border-radius: 16px;
  background: rgba(15, 46, 24, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.season-panel__featured {
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(8, 26, 10, 0.55);
  box-shadow: inset 0 0 16px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  color: #f5fdf1;
}

.season-panel__featured-link {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  text-decoration: none;
  color: #f5fdf1;
  align-items: center;
}

.season-panel__featured-link img {
  width: 130px;
  min-width: 130px;
  height: 92px;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.3);
}

.season-panel__featured-copy {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.season-panel__featured-label {
  margin: 0;
  font-size: 0.75rem;
  letter-spacing: 0.5em;
  text-transform: uppercase;
  opacity: 0.6;
}

.season-panel__featured-copy h3 {
  margin: 0;
  font-size: 1.1rem;
}

.season-panel__featured-copy p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  opacity: 0.85;
}

.season-panel__featured-copy span {
  font-size: 0.82rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  opacity: 0.8;
}

.season-panel__notice {
  margin-top: 0.75rem;
  padding: 0.65rem 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-size: 0.9rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
}

.season-panel__notice--red {
  background: rgba(255, 182, 173, 0.95);
  border-color: rgba(255, 106, 94, 0.9);
  color: #8b0000;
  box-shadow: 0 0 18px rgba(255, 110, 94, 0.4);
}

.season-panel__card .ripple {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  transform: scale(0);
  animation: ripple 0.6s ease-out;
  pointer-events: none;
}

.season-panel__card .ripple:nth-child(1) {
  animation-delay: 0s;
}

.season-panel__card .ripple:nth-child(2) {
  animation-delay: 0.05s;
}

@keyframes ripple {
  to {
    transform: scale(4);
    opacity: 0;
  }
}

.poem-block {
  margin-top: 1.5rem;
  padding: 1rem 1.1rem;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.poem-block p {
  margin: 0;
  line-height: 1.8;
  font-size: 0.95rem;
  text-indent: 1.2rem;
}

.main-right {
  flex: 0 0 30%;
  background: var(--main-right-bg, rgba(255, 255, 255, 0.07));
  border-radius: 20px;
  padding: 1.5rem 1.2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 1rem;
}

.hourly-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: inset 0 0 14px rgba(255, 255, 255, 0.16);
  overflow: hidden;
}

.hourly-media img {
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 12px;
  display: block;
}

.hourly-content {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.hourly-time {
  margin: 0;
  font-size: 0.75rem;
  letter-spacing: 0.35em;
  opacity: 0.75;
}

.hourly-label {
  margin: 0;
  font-size: 0.85rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
}

.hourly-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.hourly-description {
  margin: 0;
  line-height: 1.6;
  font-size: 0.95rem;
  opacity: 0.92;
}

.hourly-verse {
  margin: 0;
  font-size: 0.88rem;
  font-style: italic;
  opacity: 0.93;
  color: rgba(255, 255, 255, 0.85);
}

.hourly-highlight {
  margin: 0;
  font-size: 0.83rem;
  opacity: 0.85;
  color: rgba(255, 255, 255, 0.85);
}

.hourly-link {
  margin-top: 0.5rem;
  font-size: 0.78rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: var(--accent);
  text-decoration: none;
}

.hourly-link:hover {
  text-decoration: underline;
}

.layout-footer {
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: var(--footer-bg, rgba(255, 255, 255, 0.04));
  color: var(--footer-text, inherit);
  flex-wrap: wrap;
  gap: 0.6rem;
}

.footer-links {
  display: flex;
  gap: 1rem;
}

.footer-links a {
  color: inherit;
  text-decoration: none;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  opacity: 0.85;
  transition: opacity 0.3s ease;
}

.footer-links a:hover {
  opacity: 1;
}

.app-shell.spring {
  --page-bg: #e7f7e3;
  --text-color: #132b18;
  --header-bg: linear-gradient(130deg, #d5f6cf, #8ccf81);
  --accent: #2d7c46;
  --main-left-bg: linear-gradient(180deg, rgba(238, 251, 240, 0.95), rgba(198, 233, 210, 0.85));
  --main-right-bg: rgba(224, 244, 223, 0.9);
  --scene-bg: rgba(240, 251, 238, 0.95);
  --scene-spark: rgba(80, 160, 92, 0.6);
  --footer-bg: rgba(220, 238, 218, 0.92);
  --footer-text: #132b18;
}

.app-shell.summer {
  --page-bg: #e3f6fd;
  --text-color: #14354b;
  --header-bg: linear-gradient(120deg, #b3ecff, #d5f5f4);
  --accent: #57c7e8;
  --main-left-bg: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(187, 238, 243, 0.75));
  --main-right-bg: rgba(255, 255, 255, 0.58);
  --scene-bg: rgba(255, 255, 255, 0.9);
  --scene-spark: rgba(86, 214, 233, 0.65);
  --footer-bg: rgba(220, 241, 255, 0.9);
  --footer-text: #10405f;
}

.app-shell.longsummer {
  --page-bg: #fff5e1;
  --text-color: #4c3613;
  --header-bg: linear-gradient(120deg, #ffe4b5, #fff1dc);
  --accent: #f9a66c;
  --main-left-bg: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(255, 225, 178, 0.8));
  --main-right-bg: rgba(255, 255, 255, 0.65);
  --scene-bg: rgba(255, 255, 255, 0.9);
  --scene-spark: rgba(249, 166, 108, 0.5);
  --footer-bg: rgba(255, 241, 224, 0.9);
  --footer-text: #4c3613;
}

.app-shell.autumn {
  --page-bg: #fffaf1;
  --text-color: #41230f;
  --header-bg: linear-gradient(120deg, #ffd8b5, #ffe7cd);
  --accent: #e56b33;
  --main-left-bg: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(255, 224, 199, 0.9));
  --main-right-bg: rgba(255, 255, 255, 0.7);
  --scene-bg: rgba(255, 255, 255, 0.92);
  --scene-spark: rgba(229, 107, 51, 0.55);
  --footer-bg: rgba(255, 235, 215, 0.9);
  --footer-text: #41230f;
}

.app-shell.winter {
  --page-bg: #0f2342;
  --text-color: #f3f6ff;
  --header-bg: linear-gradient(120deg, #243b66, #3d5a9a);
  --accent: #8dc6ff;
  --main-left-bg: linear-gradient(180deg, rgba(48, 67, 116, 0.85), rgba(54, 80, 138, 0.75));
  --main-right-bg: rgba(38, 64, 110, 0.65);
  --scene-bg: rgba(255, 255, 255, 0.14);
  --scene-spark: rgba(150, 200, 255, 0.7);
  --footer-bg: rgba(26, 48, 99, 0.8);
  --footer-text: #f3f6ff;
}

.app-shell.harmony {
  --page-bg: #fff8e5;
  --text-color: #36250c;
  --header-bg: linear-gradient(140deg, #fddb93, #c89126);
  --accent: #c18f00;
  --main-left-bg: linear-gradient(180deg, rgba(255, 247, 224, 0.95), rgba(245, 209, 123, 0.9));
  --main-right-bg: rgba(255, 251, 233, 0.9);
  --scene-bg: rgba(255, 248, 226, 0.9);
  --scene-spark: rgba(246, 186, 79, 0.65);
  --footer-bg: rgba(255, 245, 217, 0.9);
  --footer-text: #36250c;
}

.app-shell.harmony .season-panel {
  background: linear-gradient(135deg, rgba(255, 247, 224, 0.95), rgba(246, 216, 137, 0.9));
  border-color: rgba(255, 242, 208, 0.8);
  box-shadow: 0 30px 60px rgba(196, 145, 39, 0.25);
}

.app-shell.harmony .season-panel__grid-block,
.app-shell.harmony .season-panel__masters-block,
.app-shell.harmony .season-panel__featured-block {
  background: rgba(255, 248, 232, 0.85);
  border-color: rgba(255, 234, 192, 0.8);
  box-shadow: inset 0 0 18px rgba(255, 236, 191, 0.45);
}

@keyframes pulse {
  0% {
    transform: scale(1) translateY(0);
    opacity: 0.4;
  }

  50% {
    transform: scale(1.1) translateY(-8px);
    opacity: 0.7;
  }

  100% {
    transform: scale(1) translateY(0);
    opacity: 0.4;
  }
}

@keyframes drift {
  0% {
    transform: translate(-10%, -10%);
  }

  50% {
    transform: translate(10%, 5%);
  }

  100% {
    transform: translate(-10%, -10%);
  }
}
</style>
